/**
 * RAG Conversations Store
 *
 * Manages list-scoped AI chat conversations for RAG (Retrieval-Augmented Generation).
 * Stores conversations per scope (all/inbox/list) with Supabase sync + localStorage fallback.
 *
 * Similar pattern to aiConversations$ but uses scope_type/scope_id instead of article_id.
 *
 * Usage:
 *   import { ragConversations$, getRAGConversation, addRAGMessage } from './ragConversations$';
 */

import { observable } from '@legendapp/state';
import { syncedSupabase } from '@legendapp/state/sync-plugins/supabase';
import { ObservablePersistLocalStorage } from '@legendapp/state/persist-plugins/local-storage';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';
import { createSyncCreate, createSyncUpdate, defaultSyncConfig } from './syncHelpers';

// Default AI model for new RAG conversations
const DEFAULT_AI_MODEL = 'gemini-3-flash-preview';

/**
 * RAG Conversations store with Supabase sync (local-first)
 * Private to user - filter by user_id
 *
 * Database schema uses:
 *   - scope_type: 'all' | 'inbox' | 'list'
 *   - scope_id: null for 'all'/'inbox', list UUID for 'list'
 */
export const ragConversations$ = observable(syncedSupabase({
  supabase,
  collection: 'reedle_rag_conversations',
  // Filter to only current user's conversations
  filter: (query) => {
    const userId = auth$.user?.id?.get?.() || auth$.user?.get?.()?.id;
    if (userId) {
      return query.eq('user_id', userId);
    }
    return query;
  },
  // Custom create/update with error handling (upsert fallback)
  create: createSyncCreate('reedle_rag_conversations'),
  update: createSyncUpdate('reedle_rag_conversations'),
  // Use default sync config
  ...defaultSyncConfig,
  // Enable realtime for multi-device sync
  realtime: true,
  // Persist data locally
  persist: {
    name: 'reedle-rag-conversations',
    plugin: ObservablePersistLocalStorage,
    retrySync: true,
  },
}));

/**
 * Generate scope key from scope type and id (for internal lookup)
 * This is used to find conversations in the observable store
 */
export const getScopeKey = (scopeType, scopeId = null) => {
  if (scopeType === 'all') return 'all';
  if (scopeType === 'inbox') return 'inbox';
  if (scopeType === 'list' && scopeId) return `list:${scopeId}`;
  if (scopeType === 'deck' && scopeId) return `deck:${scopeId}`;
  return 'all'; // fallback
};

/**
 * Get conversation by scope type and optional scope id
 */
export const getRAGConversation = (scopeType, scopeId = null) => {
  const userId = auth$.user?.id?.get?.() || auth$.user?.get?.()?.id;
  if (!userId) return null;

  const conversations = ragConversations$.get();
  if (!conversations || typeof conversations !== 'object') return null;

  // Find conversation matching scope
  return Object.values(conversations).find(c => {
    if (!c || c.deleted) return false;
    if (c.user_id !== userId) return false;
    if (c.scope_type !== scopeType) return false;
    // For 'list' and 'deck' scope, match scope_id; for others, scope_id should be null
    if (scopeType === 'list' || scopeType === 'deck') {
      return c.scope_id === scopeId;
    }
    return c.scope_id === null;
  }) || null;
};

/**
 * Get or create conversation for a scope
 */
export const getOrCreateRAGConversation = (scopeType, scopeId = null) => {
  const userId = auth$.user?.id?.get?.() || auth$.user?.get?.()?.id;
  if (!userId) return null;

  let conversation = getRAGConversation(scopeType, scopeId);

  if (!conversation) {
    const id = uuidv4();
    const now = new Date().toISOString();
    conversation = {
      id,
      user_id: userId,
      scope_type: scopeType,
      scope_id: (scopeType === 'list' || scopeType === 'deck') ? scopeId : null,
      messages: [],
      preferred_model: DEFAULT_AI_MODEL,
      created_at: now,
      updated_at: now,
    };
    ragConversations$[id].set(conversation);
  }

  return conversation;
};

/**
 * Add message to conversation
 */
export const addRAGMessage = (scopeType, scopeId, message) => {
  const conversation = getOrCreateRAGConversation(scopeType, scopeId);
  if (!conversation) return null;

  const messages = [...(conversation.messages || []), message];
  ragConversations$[conversation.id].messages.set(messages);
  ragConversations$[conversation.id].updated_at.set(new Date().toISOString());

  return conversation.id;
};

/**
 * Update messages in conversation
 */
export const updateRAGMessages = (scopeType, scopeId, messages) => {
  const conversation = getRAGConversation(scopeType, scopeId);
  if (!conversation) return null;

  ragConversations$[conversation.id].messages.set(messages);
  ragConversations$[conversation.id].updated_at.set(new Date().toISOString());

  return conversation.id;
};

/**
 * Set quote source for a message index
 */
export const setRAGQuoteSource = (scopeType, scopeId, messageIndex, source) => {
  const conversation = getRAGConversation(scopeType, scopeId);
  if (!conversation) return;

  const quoteSources = conversation.quote_sources || {};
  ragConversations$[conversation.id].quote_sources.set({
    ...quoteSources,
    [messageIndex]: source,
  });
  ragConversations$[conversation.id].updated_at.set(new Date().toISOString());
};

/**
 * Get quote sources for a conversation
 */
export const getRAGQuoteSources = (scopeType, scopeId) => {
  const conversation = getRAGConversation(scopeType, scopeId);
  return conversation?.quote_sources || {};
};

/**
 * Clear conversation (delete all messages)
 */
export const clearRAGConversation = (scopeType, scopeId = null) => {
  const conversation = getRAGConversation(scopeType, scopeId);
  if (!conversation) return false;

  ragConversations$[conversation.id].messages.set([]);
  ragConversations$[conversation.id].quote_sources.set({});
  ragConversations$[conversation.id].updated_at.set(new Date().toISOString());

  return true;
};

/**
 * Delete a message at specific index
 */
export const deleteRAGMessage = (scopeType, scopeId, messageIndex) => {
  const conversation = getRAGConversation(scopeType, scopeId);
  if (!conversation) return false;

  const messages = conversation.messages || [];
  if (messageIndex < 0 || messageIndex >= messages.length) return false;

  const newMessages = messages.filter((_, i) => i !== messageIndex);

  // Reindex quote sources
  const quoteSources = conversation.quote_sources || {};
  const newQuoteSources = {};
  Object.entries(quoteSources).forEach(([idx, source]) => {
    const numIdx = parseInt(idx);
    if (numIdx < messageIndex) {
      newQuoteSources[numIdx] = source;
    } else if (numIdx > messageIndex) {
      newQuoteSources[numIdx - 1] = source;
    }
  });

  ragConversations$[conversation.id].messages.set(newMessages);
  ragConversations$[conversation.id].quote_sources.set(newQuoteSources);
  ragConversations$[conversation.id].updated_at.set(new Date().toISOString());

  return true;
};

/**
 * Update preferred model for a scope
 */
export const setRAGPreferredModel = (scopeType, scopeId, model) => {
  const conversation = getOrCreateRAGConversation(scopeType, scopeId);
  if (!conversation) return null;

  ragConversations$[conversation.id].preferred_model.set(model);
  ragConversations$[conversation.id].updated_at.set(new Date().toISOString());

  return conversation.id;
};

/**
 * Update preferred language for a scope
 * Note: Language is stored in localStorage separately (via AILearnContext pattern)
 * This is for backwards compatibility - we don't add a language column to DB
 */
export const setRAGPreferredLanguage = (scopeType, scopeId, language) => {
  // Language preference is handled via localStorage in RAGChatContext
  // This function exists for API compatibility but is a no-op for DB sync
  try {
    localStorage.setItem('reedle-ai-language', language);
  } catch {
    // Ignore storage errors
  }
};

/**
 * Delete conversation entirely (soft delete)
 */
export const deleteRAGConversation = (scopeType, scopeId = null) => {
  const conversation = getRAGConversation(scopeType, scopeId);
  if (!conversation) return false;

  ragConversations$[conversation.id].deleted.set(true);
  ragConversations$[conversation.id].updated_at.set(new Date().toISOString());

  return true;
};
